import moment from 'moment';
import { action, extendObservable } from 'mobx';
import { browserHistory } from 'react-router';
import { get, pickBy, omit, pick } from 'lodash';

import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import NotificationService from '../../../core/services/NotificationService';
import ContractProductsService from './ContractProductsService';
import { isCNPJ, isCPF, isRequired } from '../../../core/validations';
import { BRAZIL, CONTRACT_PRODUCTS } from '../../../core/consts';

class ConfirmRenewalContractFormService {
  fetch = new FetchService();
  submit = new FetchService();
  form = new FormService();
  productsService = new ContractProductsService();

  constructor() {
    extendObservable(this, {
      isV2: false,
      type: '',
    });
    this.form.validations = {
      dueDay: [isRequired],
    };
  }

  init = action((contractId) => {
    this.contractId = contractId;
    this.form.setInitialValues({});
    this.form.reset();
    this.productsService.init('');
    this.load(contractId);
  });

  load = action(async (contractId) => {
    this.contractId = contractId;
    this.fetch.fetch({ url: `/contracts/${this.contractId}`, apiVersion: 'v2' }).then(async () => {
      if (this.fetch.data) {
        this.type = this.fetch.data.type;
        this.isV2 = this.fetch.data.version === 'v2';
        this.form.setInitialValues({
          ...pickBy(this.fetch.data, (value) => value !== null && value !== undefined),
          startsAt: moment(this.fetch.data.startsAt).utc(),
          ...(this.fetch.data.endsAt && { endsAt: moment(this.fetch.data.endsAt).utc() }),
        });

        this.productsService.init(this.fetch.data.type);
        await this.productsService.load();

        if (this.fetch.data.type === 'UPFRONT' && this.fetch.data.version === 'v2') {
          const aiExercisesProduct = this.fetch.data.products.find((p) => p.hs_sku === CONTRACT_PRODUCTS.AI_EXERCISES);

          this.productsService.contractProducts = [
            ...this.productsService.contractProducts
              .filter((product) => {
                return !!this.fetch.data.products.find((p) => p.hs_sku === product.hs_sku);
              })
              .map((product, index) => {
                const currentProduct = this.fetch.data.products.find((p) => p.hs_sku === product.hs_sku);
                return {
                  rowKey: index + 1,
                  ...currentProduct,
                  isEnabled: true,
                };
              }),
            ...(aiExercisesProduct ? [aiExercisesProduct] : []),
          ];
        }

        if (this.fetch.data.type === 'POST_PAYMENT' || this.fetch.data.version !== 'v2') {
          this.productsService.contractProducts = this.fetch.data.products.map((product) => ({
            ...product,
            rowKey: product.id,
            ...(product.beginningOfTerm && { beginningOfTerm: moment(product.beginningOfTerm).add(3, 'hours') }),
          }));
        }
      }
    });
  });

  onChange = action((path, value) => {
    if (path === 'contractFile') {
      this.form.setValue('contractUrl', null);
    }
    if (path === 'documentNumber' && BRAZIL.includes(get(this.form.getValues(), 'country', ''))) {
      if (get(this.form.getValues(), 'documentNumber', '').length > 11) {
        this.form.validations.documentNumber = [isRequired, isCNPJ];
      } else {
        this.form.validations.documentNumber = [isRequired, isCPF];
      }
    }
    if (path === 'country' && !BRAZIL.includes(value)) {
      this.form.validations.documentNumber = [isRequired];
    }

    this.form.setValue(path, value);
  });

  handleSubmit = () => {
    this.form.submitted = true;
    if (this.form.errors) {
      NotificationService.addNotification('Fill the required fields', 'error');
      return;
    }
    if (!this.productsService.contractProducts.length) {
      NotificationService.addNotification('At least one product must be registered to activate the contract.', 'error');
      return;
    }

    const formValues = pick(this.form.getValues(), [
      'documentNumber',
      'socialReason',
      'country',
      'state',
      'city',
      'address',
      'district',
      'number',
      'zipCode',
      'emails',
      'phone',
      'allowedPaymentMethods',
      'startsAt',
      'endsAt',
      'dueDay',
    ]);

    this.submit
      .fetch({
        method: 'put',
        url: `/contracts/${this.contractId}/confirm-renewal`,
        apiVersion: 'v2',
        body: {
          ...omit(formValues, ['status', 'deleted', 'createdAt', 'updatedAt', 'id']),
          products: this.productsService.contractProducts
            .filter((product) => {
              if (this.type === 'UPFRONT') {
                return product.isEnabled;
              }

              return true;
            })
            .map((product) => ({
              ...omit(product, ['rowKey', 'id']),
              description: product.description,
              hs_sku: product.hs_sku,
              amount: product.amount ? Number(product.amount) : undefined,
              freeAmount: product.freeAmount ? Number(product.freeAmount) : undefined,
              finalUnitPrice: Number(product.finalUnitPrice),
              installments: product.installments ? Number(product.installments) : undefined,
              paidInstallments: product.paidInstallments ? Number(product.paidInstallments) : undefined,
            })),
        },
      })
      .then(() => {
        if (this.submit.data) {
          if (this.form.getValue('contractFile')) {
            const formData = new FormData();
            formData.append('contractFile', this.form.getValue('contractFile'));
            this.submit
              .fetch({
                method: 'patch',
                url: `/contracts/${this.submit.data.id}/upload`,
                apiVersion: 'v2',
                body: formData,
              })
              .then(() => {
                if (this.submit.data) {
                  NotificationService.addNotification(`Contract renewal successfully confirmed.`, 'success');
                  browserHistory.push('/contracts');
                }
              });
          } else {
            NotificationService.addNotification(`Contract renewal successfully confirmed.`, 'success');
            browserHistory.push('/contracts');
          }
        }
        if (this.submit.error) {
          NotificationService.addNotification(this.submit.error || `Error renewal contract.`, 'error');
        }
      });
  };
}

const confirmRenewalFormService = new ConfirmRenewalContractFormService();

export default confirmRenewalFormService;
